<script setup lang="ts">
import { Image, Pencil, X } from "@lucide/vue";
import { computed, inject, ref } from "vue";
import { useI18n } from "../composables/useI18n";
import { ON_REQUEST_MEDIA_KEY } from "../keys";

const props = defineProps<{
  imageUrl: string;
}>();

const emit = defineEmits<{
  "set-url": [url: string, templateWidth?: number];
}>();

const { t } = useI18n();

/**
 * Host-supplied media picker (set via editor `init({ onRequestMedia })`).
 * When wired, all overlay-image picking is delegated to the host so consumers
 * can upload to their own storage and return a public URL — no data-URL
 * round-trip is stored in the template JSON. Falls back to a local
 * FileReader → data-URL flow when no callback is provided so the picker still
 * works in standalone setups.
 */
const onRequestMedia = inject(ON_REQUEST_MEDIA_KEY, null);
const useHostPicker = computed(() => onRequestMedia !== null);

const fileRef = ref<HTMLInputElement | null>(null);
const isDragging = ref(false);

const MAX_DATA_URL_CHARS = 2_400_000;

function pickFiles(files: FileList | File[] | null): void {
  const file = files?.[0];
  if (!file || !file.type.startsWith("image/")) return;
  const reader = new FileReader();
  reader.onload = () => {
    const url = typeof reader.result === "string" ? reader.result : "";
    if (url.length > MAX_DATA_URL_CHARS) return;
    emit("set-url", url);
    if (fileRef.value) fileRef.value.value = "";
  };
  reader.readAsDataURL(file);
}

async function browseMedia(): Promise<void> {
  const result = await onRequestMedia?.({ accept: ["images"] });
  if (result?.url) {
    emit("set-url", result.url, result.templateWidth);
  }
}

function onDrop(e: DragEvent): void {
  isDragging.value = false;
  if (useHostPicker.value) {
    // Host owns the upload pipeline — defer to it instead of reading a local
    // data URL we'd then need to discard.
    void browseMedia();
    return;
  }
  pickFiles(e.dataTransfer?.files ?? null);
}

function onBrowse(): void {
  if (useHostPicker.value) {
    void browseMedia();
    return;
  }
  fileRef.value?.click();
}

function onReplace(): void {
  if (useHostPicker.value) {
    void browseMedia();
    return;
  }
  fileRef.value?.click();
}

function onRemove(): void {
  emit("set-url", "");
}
</script>

<template>
  <div
    class="tpl:mt-4 tpl:rounded-lg tpl:border tpl:border-dashed tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg)] tpl:p-3"
    role="region"
    :aria-label="t.popupDesign.overlayImagePanelAriaLabel"
  >
    <input
      ref="fileRef"
      type="file"
      class="tpl:sr-only"
      tabindex="-1"
      accept="image/jpeg,image/png,image/gif,image/webp,image/avif,image/svg+xml"
      @change="pickFiles(($event.target as HTMLInputElement).files)"
    />

    <template v-if="!props.imageUrl">
      <button
        type="button"
        class="tpl:flex tpl:w-full tpl:flex-col tpl:items-center tpl:justify-center tpl:gap-2 tpl:rounded-md tpl:border-none tpl:bg-transparent tpl:px-3 tpl:py-8 tpl:text-center tpl:text-[var(--tpl-text)] tpl:outline-none tpl:transition-colors hover:tpl:bg-[var(--tpl-bg-hover)] focus-visible:tpl:ring-2 focus-visible:tpl:ring-[var(--tpl-primary)] focus-visible:tpl:ring-offset-2 focus-visible:tpl:ring-offset-[var(--tpl-bg)]"
        :class="
          isDragging
            ? 'tpl:bg-[var(--tpl-primary-light)] tpl:text-[var(--tpl-primary)]'
            : ''
        "
        @dragenter.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @dragover.prevent="isDragging = true"
        @drop.prevent="onDrop"
        @click="onBrowse"
      >
        <Image
          :size="28"
          :stroke-width="1.5"
          class="tpl:text-[var(--tpl-text-muted)]"
        />
        <span class="tpl:text-xs tpl:leading-snug tpl:text-[var(--tpl-text)]">
          {{ t.popupDesign.overlayImageDropLineBefore }}
          <span class="tpl:underline tpl:decoration-[var(--tpl-text-muted)]">{{
            t.popupDesign.overlayImageBrowse
          }}</span>
          {{ t.popupDesign.overlayImageDropLineAfter }}
        </span>
        <span class="tpl:text-[11px] tpl:text-[var(--tpl-text-muted)]">{{
          t.popupDesign.overlayImageFormats
        }}</span>
      </button>
    </template>

    <template v-else>
      <div class="tpl:mb-3 tpl:flex tpl:gap-2">
        <div
          class="tpl:flex tpl:min-h-0 tpl:flex-1 tpl:flex-col tpl:overflow-hidden tpl:rounded-lg tpl:border tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg-elevated)]"
          role="img"
          :aria-label="t.popupDesign.overlayImageDesktopPreview"
        >
          <div
            class="tpl:flex tpl:gap-1 tpl:border-b tpl:border-[var(--tpl-border)] tpl:px-2 tpl:py-1.5"
          >
            <span
              class="tpl:h-1.5 tpl:w-1.5 tpl:rounded-full tpl:bg-[var(--tpl-border-light)]"
            />
            <span
              class="tpl:h-1.5 tpl:w-1.5 tpl:rounded-full tpl:bg-[var(--tpl-border-light)]"
            />
            <span
              class="tpl:h-1.5 tpl:w-1.5 tpl:rounded-full tpl:bg-[var(--tpl-border-light)]"
            />
          </div>
          <div
            class="tpl:flex tpl:aspect-[16/9] tpl:max-h-[72px] tpl:items-center tpl:justify-center tpl:bg-[var(--tpl-bg)] tpl:p-1"
          >
            <img
              :src="props.imageUrl"
              alt=""
              class="tpl:max-h-full tpl:max-w-full tpl:rounded tpl:object-contain"
            />
          </div>
        </div>
        <div
          class="tpl:flex tpl:w-[52px] tpl:shrink-0 tpl:flex-col tpl:overflow-hidden tpl:rounded-lg tpl:border tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg-elevated)]"
          role="img"
          :aria-label="t.popupDesign.overlayImageMobilePreview"
        >
          <div
            class="tpl:flex tpl:gap-0.5 tpl:border-b tpl:border-[var(--tpl-border)] tpl:px-1 tpl:py-1"
          >
            <span
              class="tpl:h-1 tpl:w-1 tpl:rounded-full tpl:bg-[var(--tpl-border-light)]"
            />
            <span
              class="tpl:h-1 tpl:w-1 tpl:rounded-full tpl:bg-[var(--tpl-border-light)]"
            />
            <span
              class="tpl:h-1 tpl:w-1 tpl:rounded-full tpl:bg-[var(--tpl-border-light)]"
            />
          </div>
          <div
            class="tpl:flex tpl:aspect-[9/14] tpl:max-h-[72px] tpl:items-center tpl:justify-center tpl:bg-[var(--tpl-bg)] tpl:p-0.5"
          >
            <img
              :src="props.imageUrl"
              alt=""
              class="tpl:max-h-full tpl:max-w-full tpl:rounded-sm tpl:object-cover"
            />
          </div>
        </div>
      </div>

      <div class="tpl:flex tpl:flex-wrap tpl:gap-2">
        <!-- <button
          type="button"
          class="tpl:inline-flex tpl:flex-1 tpl:items-center tpl:justify-center tpl:gap-1.5 tpl:rounded-lg tpl:border tpl:border-[var(--tpl-border)] tpl:bg-transparent tpl:px-2 tpl:py-2 tpl:text-[11px] tpl:font-medium tpl:text-[var(--tpl-text-muted)] tpl:transition-colors hover:tpl:border-[var(--tpl-text-muted)] hover:tpl:text-[var(--tpl-text)]"
          @click="onCrop"
        >
          <Crop :size="14" :stroke-width="1.75" />
          {{ t.popupDesign.overlayImageCrop }}
        </button> -->
        <button
          type="button"
          class="tpl:inline-flex tpl:flex-1 tpl:items-center tpl:justify-center tpl:gap-1.5 tpl:rounded-lg tpl:border tpl:border-[var(--tpl-border)] tpl:bg-transparent tpl:px-2 tpl:py-2 tpl:text-[11px] tpl:font-medium tpl:text-[var(--tpl-text-muted)] tpl:transition-colors hover:tpl:border-[var(--tpl-text-muted)] hover:tpl:text-[var(--tpl-text)]"
          @click="onReplace"
        >
          <Pencil :size="14" :stroke-width="1.75" />
          {{ t.popupDesign.overlayImageReplace }}
        </button>
        <button
          type="button"
          class="tpl:inline-flex tpl:flex-1 tpl:items-center tpl:justify-center tpl:gap-1.5 tpl:rounded-lg tpl:border tpl:border-[var(--tpl-border)] tpl:bg-transparent tpl:px-2 tpl:py-2 tpl:text-[11px] tpl:font-medium tpl:text-[var(--tpl-text-muted)] tpl:transition-colors hover:tpl:border-[var(--tpl-danger)] hover:tpl:text-[var(--tpl-danger)]"
          @click="onRemove"
        >
          <X :size="14" :stroke-width="1.75" />
          {{ t.popupDesign.overlayImageRemove }}
        </button>
      </div>
    </template>
  </div>
</template>
